import { useRef, useState } from 'preact/hooks';
import { overdue, dueSoon, scheduled, justFed, activeCount, type BoardEntry } from '@/state/board';
import { openTimers, nowTick } from '@/state/data';
import { logFeeding, undoFeeding, closeTimer } from '@/lib/mutations';
import { syncNow } from '@/lib/sync';
import { openSheet, showToast } from '@/state/ui';
import { SnoozeSheet } from '@/components/SnoozeSheet';
import { FeedingCard } from '@/components/FeedingCard';
import { Icon } from '@/components/Icon';
import { initials, avatarClass } from '@/types';
import { timeShort, dueLabel } from '@/lib/due';
import { animals } from '@/state/data';

function headDate(): string {
  const d = new Date();
  return `${d.toLocaleDateString([], { weekday: 'long' })} · ${timeShort(d)}`;
}

export function NowScreen() {
  void nowTick.value;
  const [pull, setPull] = useState(0);
  const pullStart = useRef<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const onFed = async (entry: BoardEntry) => {
    const logId = await logFeeding({
      animalId: entry.animal.id,
      scheduleId: entry.schedule.id,
      source: 'manual',
    });
    showToast(
      {
        kind: 'undo',
        message: `${entry.animal.name} fed`,
        actionLabel: 'Undo',
        onAction: () => void undoFeeding(logId),
      },
      5000
    );
  };

  const onSnooze = (entry: BoardEntry) => {
    openSheet({ title: `Snooze ${entry.animal.name}`, content: <SnoozeSheet entry={entry} /> });
  };

  // Pull-to-refresh paw (animation #20)
  const onTouchStart = (e: TouchEvent) => {
    if (scrollRef.current && scrollRef.current.scrollTop <= 0) pullStart.current = e.touches[0].clientY;
  };
  const onTouchMove = (e: TouchEvent) => {
    if (pullStart.current == null) return;
    const dy = e.touches[0].clientY - pullStart.current;
    setPull(Math.max(0, Math.min(90, dy * 0.5)));
  };
  const onTouchEnd = () => {
    if (pull > 48) void syncNow();
    setPull(0);
    pullStart.current = null;
  };

  const timers = openTimers.value;
  const now = Date.now();
  const empty =
    !overdue.value.length && !dueSoon.value.length && !scheduled.value.length && !timers.length;

  return (
    <div class="screen screen--fade-in">
      <div class="now-head">
        <p class="t-eyebrow">{headDate()}</p>
        <h1 class="t-h1">
          Now <span class="count">· {activeCount.value} active</span>
        </h1>
      </div>

      {empty ? (
        <div class="now-empty">
          <svg class="now-empty__art" width="96" height="72" viewBox="0 0 96 72" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <ellipse cx="48" cy="52" rx="30" ry="14" />
            <path d="M30 48c0-14 8-24 18-24s18 10 18 24" />
            <circle cx="42" cy="38" r="1.6" fill="currentColor" stroke="none" />
            <path d="M52 40c2 1 4 1 6 0" />
            <path d="M70 26c3-2 6-2 8 0M74 24v6" stroke-width="1.5" />
          </svg>
          <h2 class="now-empty__title">Everyone's resting</h2>
          <p class="now-empty__sub">No feedings on the clock. The board fills in as schedules come due.</p>
        </div>
      ) : (
        <div
          class="board"
          ref={scrollRef}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {pull > 0 && (
            <div class="ptr" style={{ height: `${pull}px` }}>
              <Icon name="paw" size={22 + pull / 5} />
            </div>
          )}

          {overdue.value.length > 0 && (
            <>
              <p class="t-eyebrow board__label board__label--alert">Overdue · {overdue.value.length}</p>
              {overdue.value.map((e) => (
                <FeedingCard key={e.schedule.id} entry={e} onFed={onFed} onSnooze={onSnooze} />
              ))}
            </>
          )}

          {dueSoon.value.length > 0 && (
            <>
              <p class="t-eyebrow board__label">Due soon</p>
              {dueSoon.value.map((e) => (
                <FeedingCard key={e.schedule.id} entry={e} onFed={onFed} onSnooze={onSnooze} />
              ))}
            </>
          )}

          {timers.length > 0 && (
            <>
              <p class="t-eyebrow board__label">Ad-hoc timers</p>
              {timers.map((t) => {
                const due = new Date(t.due_at);
                const isDue = due.getTime() <= now;
                const animal = t.animal_id ? animals.value.get(t.animal_id) : null;
                return (
                  <div key={t.id} class="timer-card">
                    <span class={`timer-card__time ${isDue ? 'timer-card__time--due' : ''}`}>
                      <span class="dot" />
                      {timeShort(due)}
                    </span>
                    <div class="timer-card__label">
                      {animal ? `${animal.name} · ` : ''}
                      {t.label}
                      {isDue && <span class="t-meta"> · {dueLabel(due)}</span>}
                    </div>
                    <button
                      class="iconbtn"
                      aria-label="Done"
                      onClick={() => void closeTimer(t.id, 'completed')}
                    >
                      <Icon name="check" size={18} />
                    </button>
                    <button
                      class="iconbtn"
                      aria-label="Cancel timer"
                      onClick={() => void closeTimer(t.id, 'canceled')}
                    >
                      <Icon name="x" size={18} />
                    </button>
                  </div>
                );
              })}
            </>
          )}

          {justFed.value.length > 0 && (
            <>
              <p class="t-eyebrow board__label board__label--accent">Just fed</p>
              {justFed.value.map(({ log, animal }) => (
                <div key={log.id} class="feed-card feed-card--fed">
                  <span class={`avatar avatar--sm ${avatarClass(animal.category)}`}>{initials(animal.name)}</span>
                  <div class="timer-card__label">
                    {animal.name}
                    {animal.species ? ` · ${animal.species.toLowerCase()}` : ''}
                  </div>
                  <span class="feed-card__fed-time">
                    {timeShort(new Date(log.fed_at))}
                    {log.fed_by ? ` · by ${log.fed_by}` : ''}
                  </span>
                </div>
              ))}
            </>
          )}

          {scheduled.value.length > 0 && (
            <>
              <p class="t-eyebrow board__label">Later today</p>
              {scheduled.value.slice(0, 20).map((e) => (
                <FeedingCard key={e.schedule.id} entry={e} onFed={onFed} onSnooze={onSnooze} />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
